/**
 * Переводы. Четыре языка: немецкий, молдавский (по письму — румынский,
 * отсюда ro-MD), русский и английский. Английский добавлен заказчиком уже
 * после спецификации, где значилось три.
 *
 * Строки лежат здесь с первого дня намеренно: вынести их задним числом
 * втрое дороже, а немецкий в среднем на треть длиннее русского — вёрстку
 * надо проверять на нём с самого начала, а не перед сдачей.
 */

export const LANGUAGES = ['de', 'ro-MD', 'ru', 'en'] as const
export type Lang = (typeof LANGUAGES)[number]

export const LANG_LABEL: Record<Lang, string> = {
  de: 'Deutsch',
  'ro-MD': 'Română',
  ru: 'Русский',
  en: 'English',
}

export type Dict = {
  appName: string
  signIn: string
  signInSub: string
  email: string
  password: string
  signInAction: string
  signingIn: string
  /** Экран принятия приглашения. */
  inviteHello: (name: string) => string
  inviteSetPassword: string
  inviteRepeat: string
  inviteMismatch: string
  invitePasswordRule: string
  inviteDone: string
  inviteGone: string
  inviteGo: string
  signOut: string
  twoStep: string
  twoStepOn: string
  twoStepOff: string
  twoStepCode: string
  twoStepHint: string
  twoStepKey: string
  twoStepOpenApp: string
  twoStepNeeded: string
  twoStepHave: string
  profile: string

  fleet: string
  events: string
  reports: string
  manage: string

  moving: string
  parked: string
  blocked: string
  /**
   * Трекер молчит. Никогда не «нет связи» / «offline» (решение владельца
   * 28.09): на стоянке FMC150 не пишет по замыслу, и слово пугало клиентов.
   */
  quiet: string
  quietSince: (when: string) => string

  speed: string
  voltage: string
  voltageRow: string
  odometer: string
  fuel: string
  charge: string
  chargeState: string
  chargeOn: string
  chargePlugged: string
  chargeOff: string
  refreshAction: string
  refreshAsked: string
  ignition: string
  relay: string
  lastSeen: string
  on: string
  off: string
  relayRaised: string
  relayClear: string
  unknown: string

  back: string
  noVehicles: string
  comingSoon: string
  networkError: string
  /** Отказ без внятного текста: сервер сказал что-то своё, служебное. */
  unclearError: string
  language: string
  /** Тема оформления. */
  theme: string
  themeLight: string
  themeDark: string
  themeSystem: string
  sheetHandle: string
  /** Кнопки поверх карты: вернуться к машине и показать себя. */
  backToVehicle: string
  whereAmI: string

  blockAction: string
  unblockAction: string
  confirmBlockTitle: string
  confirmBlockText: string
  confirmUnblockTitle: string
  confirmUnblockText: string
  engineWarning: string
  confirmYes: string
  confirmAction: string
  confirmNo: string
  working: string

  period: string
  last24h: string
  last7d: string
  last30d: string
  trackTooLong: (days: number) => string
  trips: string
  stops: string
  summary: string
  track: string
  showTrack: string
  hideTrack: string
  nothingFound: string
  loading: string
  distance: string
  duration: string
  avgSpeed: string
  maxSpeed: string
  engineHours: string
  from: string
  to: string
  search: string
  searchPlate: string
  nothingMatches: string
  pickVehicle: string
  fleetTotal: string
  scheduleRun: string
  runBlocked: string
  runReleased: string
  runRefused: string
  runFailed: string
  eventTypes: Record<string, string>

  /**
   * Подтипы тревоги. Traccar шлёт их не типом события, а атрибутом `alarm`
   * внутри события типа `alarm` — по одному событию на подтип.
   */
  alarms: Record<string, string>

  /** Единицы измерения. До выгрузки были зашиты по-русски во всех языках. */
  unitKm: string
  unitLiter: string
  rpm: string
  fuelUsedSinceInstall: string
  tankMissing: string
  unitMeter: string
  unitHour: string
  unitMinute: string

  exportAction: string
  exportTrack: string
  exporting: string

  shareAction: string
  shareHowLong: string
  routeAction: string
  routeApple: string
  routeGoogle: string
  routeFixed: string
  etaAction: string
  etaWithTraffic: string
  etaNoLocation: string
  addressUnknown: string
  shareCopied: string
  shareUntil: string
  shareGone: string
  shareRevoke: string
  notifyEnable: string
  notifyOn: string
  notifyBlocked: string
  subscribe: string

  /** Заголовки колонок в выгружаемом файле. */
  csv: {
    vehicle: string
    start: string
    end: string
    duration: string
    distance: string
    avgSpeed: string
    maxSpeed: string
    addressFrom: string
    addressTo: string
    address: string
    engineHours: string
    fuelUsed: string
    time: string
    event: string
    latitude: string
    longitude: string
    speed: string
    ignition: string
    relay: string
    energy: string
  }

  /** Экраны управления — их видит только администратор. */
  mng: {
    vehicles: string
    people: string
    addVehicle: string
    name: string
    fullName: string
    imei: string
    vin: string
    swapTracker: string
    /** Кнопка, открывающая форму: «Заменить трекер…» — она ещё ничего не делает. */
    swapTrackerOpen: string
    /** Кнопка самого действия: она делает замену, и это её единственный смысл. */
    swapTrackerDo: string
    swapTrackerHint: string
    swapTrackerDone: string
    plate: string
    brand: string
    brandUnset: string
    model: string
    modelHint: string
    tank: string
    tankHint: string
    tankAsk: string
    tankFromLink: string
    tankUnset: string
    login: string
    password: string
    newPassword: string
    role: string
    roleAdmin: string
    roleDispatcher: string
    roleManager: string
    roleAdminHint: string
    roleDispatcherHint: string
    roleManagerHint: string
    access: string
    noAccess: string
    disabled: string
    enable: string
    disable: string
    save: string
    remove: string
    removeVehicle: string
    removeVehicleText: string
    removePerson: string
    removePersonText: string
    saved: string
    noPeople: string
    invitePerson: string
    inviteEmail: string
    inviteHint: string
    inviteSend: string
    invited: string
    inviteUnsent: string
    inviteExpired: string
    inviteResend: string
    inviteRevoke: string
    inviteSentOk: string

    zones: string
    schedule: string
    scheduleOff: string
    scheduleNotRunning: string
    workFrom: string
    workUntil: string
    workDays: string
    weekdays: string[]
    journal: string
    journalEmpty: string
    didBlock: string
    didUnblock: string
    notConfirmed: string
    addZone: string
    radius: string
    pickOnMap: string
    pickAgain: string
    zoneWatchers: string
    removeZone: string
    removeZoneText: string
    notACircle: string
    noZones: string
  }

  /**
   * Экран «Компании» — список фирм и заведение новой. Виден только
   * администратору сервера, а не любому, у кого есть `canManage`: обычный
   * администратор фирмы своих соседей по серверу видеть не должен.
   */
  companies: string
  companiesEmpty: string
  companiesNew: string
  companyName: string
  ownerEmail: string
  deviceLimit: string
  userLimit: string
  expiry: string
  unlimited: string
  create: string
  disable: string
  enable: string
  termDaysLeft: (days: number) => string
  termExpired: string
  termUnlimited: string
  machinesOfLimit: (n: number, limit: string) => string
  people: string
  never: string
  invitePending: string
  inviteUnsent: string

  /**
   * «Нагрузка» и «Журнал» — вкладки того же экрана компаний, тоже только
   * для администратора сервера: журнал показывает действия над чужими
   * фирмами, а нагрузка — состояние сервера в целом.
   */
  load: string
  journal: string
  journalEmpty: string
  healthVersion: string
  healthUptime: string
  healthRooms: string
  healthDevices: string
  healthOnline: string
  healthLastPosition: string
  actCreateOrg: string
  actDisable: string
  actEnable: string
  actSetLimits: string
  actIssueKey: string
  actRevokeKey: string
  issueKey: string
  keyShownOnce: string
  keyName: string
}

const de: Dict = {
  appName: 'Fuhrpark',
  signIn: 'Anmelden',
  signInSub: 'Fuhrparkverwaltung',
  email: 'Benutzername',
  password: 'Passwort',
  signInAction: 'Anmelden',
  signingIn: 'Anmeldung läuft…',
  inviteHello: (name) => `Hallo, ${name}`,
  inviteSetPassword: 'Legen Sie Ihr Passwort fest',
  inviteRepeat: 'Passwort wiederholen',
  inviteMismatch: 'Die Passwörter stimmen nicht überein.',
  invitePasswordRule: 'Mindestens 10 Zeichen.',
  inviteDone: 'Passwort festgelegt. Sie können sich jetzt anmelden.',
  inviteGone: 'Dieser Link ist nicht mehr gültig. Bitten Sie um einen neuen.',
  inviteGo: 'Zur Anmeldung',
  signOut: 'Abmelden',
  twoStep: 'Anmeldung in zwei Schritten',
  twoStepOn: 'Eingeschaltet',
  twoStepOff: 'Ausschalten',
  twoStepCode: 'Code aus der App',
  twoStepHint: 'Schlüssel in die Authenticator-App eintragen und den aktuellen Code eingeben.',
  twoStepKey: 'Schlüssel',
  twoStepOpenApp: 'In der App öffnen',
  twoStepNeeded: 'Code aus der Authenticator-App eingeben.',
  twoStepHave: 'Ich habe die Anmeldung in zwei Schritten',
  profile: 'Konto',

  fleet: 'Fahrzeuge',
  events: 'Ereignisse',
  reports: 'Berichte',
  manage: 'Verwaltung',

  moving: 'in Fahrt',
  parked: 'steht',
  blocked: 'Start gesperrt',
  quiet: 'keine neuen Daten',
  quietSince: (when) => `keine neuen Daten seit ${when}`,

  speed: 'km/h',
  voltage: 'Volt',
  voltageRow: 'Spannung',
  odometer: 'km Stand',
  fuel: 'Kraftstoff',
  charge: 'Ladung',
  chargeState: 'Ladevorgang',
  chargeOn: 'lädt',
  chargePlugged: 'Kabel steckt',
  chargeOff: 'nicht verbunden',
  refreshAction: 'Standort aktualisieren',
  refreshAsked: 'Wird aktualisiert — Fahrzeug meldet sich',
  ignition: 'Zündung',
  relay: 'Sperrrelais',
  lastSeen: 'Letzter Kontakt',
  on: 'ein',
  off: 'aus',
  relayRaised: 'aktiv',
  relayClear: 'gelöst',
  unknown: 'unbekannt',

  back: 'Fahrzeuge',
  noVehicles: 'Ihnen sind noch keine Fahrzeuge zugewiesen.',
  comingSoon: 'Dieser Bereich wird in einer späteren Stufe gebaut.',
  networkError: 'Server nicht erreichbar. Bitte in einer Minute erneut versuchen.',
  unclearError: 'Der Server hat die Anfrage abgelehnt. Bitte Angaben prüfen oder später erneut versuchen.',
  language: 'Sprache',
  theme: 'Erscheinungsbild',
  themeLight: 'Hell',
  themeDark: 'Dunkel',
  themeSystem: 'Wie im System',
  sheetHandle: 'Höhe der Liste ändern',
  backToVehicle: 'Zurück zum Fahrzeug',
  whereAmI: 'Mein Standort',
  blockAction: 'Start sperren',
  unblockAction: 'Sperre aufheben',
  confirmBlockTitle: 'Start sperren?',
  confirmBlockText: 'kann nicht mehr gestartet werden, bis die Sperre aufgehoben wird.',
  confirmUnblockTitle: 'Sperre aufheben?',
  confirmUnblockText: 'kann wieder gestartet werden. Der Befehl geht sofort an den Tracker.',
  engineWarning: 'Ein bereits laufender Motor läuft weiter — das System stellt kein fahrendes Fahrzeug ab.',
  confirmYes: 'Ja, sperren',
  confirmAction: 'Bestätigen',
  confirmNo: 'Abbrechen',
  working: 'Wird ausgeführt…',
  period: 'Zeitraum',
  last24h: '24 Stunden',
  last7d: '7 Tage',
  last30d: '30 Tage',
  trackTooLong: (days: number) =>
    `Die Strecke wird für höchstens ${days} Tage angezeigt. Wählen Sie einen kürzeren Zeitraum.`,
  trips: 'Fahrten',
  stops: 'Standzeiten',
  summary: 'Übersicht',
  track: 'Strecke',
  showTrack: 'Strecke auf der Karte',
  hideTrack: 'Strecke ausblenden',
  nothingFound: 'Keine Daten für diesen Zeitraum.',
  loading: 'Wird geladen…',
  distance: 'Strecke',
  duration: 'Dauer',
  avgSpeed: 'Ø',
  maxSpeed: 'max.',
  engineHours: 'Motorstunden',
  from: 'von',
  to: 'bis',
  search: 'Fahrzeug suchen',
  searchPlate: 'Kennzeichen, Marke, Modell',
  nothingMatches: 'Kein Fahrzeug mit diesem Namen.',
  pickVehicle: 'Wählen Sie ein Fahrzeug aus der Liste.',
  fleetTotal: 'Gesamt für den Fuhrpark',
  scheduleRun: 'Zeitplan',
  runBlocked: 'gesperrt',
  runReleased: 'freigegeben',
  runRefused: 'abgelehnt',
  runFailed: 'Fehler',
  eventTypes: {
    deviceOnline: 'Verbindung hergestellt',
    deviceOffline: 'keine neuen Daten',
    deviceMoving: 'Fahrt begonnen',
    deviceStopped: 'angehalten',
    deviceOverspeed: 'Geschwindigkeit überschritten',
    geofenceEnter: 'Zone betreten',
    geofenceExit: 'Zone verlassen',
    ignitionOn: 'Zündung ein',
    ignitionOff: 'Zündung aus',
    commandResult: 'Antwort des Trackers',
    queuedCommandSent: 'Befehl zugestellt',
    unaccompaniedMotion: 'Bewegung ohne Zündung',
    alarm: 'Alarm',
  },
  alarms: {
    tow: 'Abschleppen erkannt',
    powerCut: 'Stromversorgung getrennt',
    jamming: 'GSM-Störsender',
    accident: 'Aufprall erkannt',
  },
  unitKm: 'km',
  unitLiter: 'l',
  rpm: 'U/min',
  fuelUsedSinceInstall: 'Verbraucht seit Einbau',
  tankMissing: 'Liter im Tank werden nicht berechnet: Das Tankvolumen fehlt. Tragen Sie es in den Fahrzeugdaten ein.',
  unitMeter: 'm',
  unitHour: 'Std.',
  unitMinute: 'Min.',
  exportAction: 'Als CSV speichern',
  exportTrack: 'Strecke als CSV',
  exporting: 'Wird vorbereitet…',
  shareAction: 'Standort teilen',
  shareHowLong: 'Für wie lange?',
  routeAction: 'Route planen',
  routeApple: 'Apple Karten',
  routeGoogle: 'Google Maps',
  routeFixed: 'Route zum zuletzt bekannten Punkt. Fährt das Fahrzeug weiter, ändert sie sich nicht.',
  etaAction: 'Fahrzeit berechnen',
  etaWithTraffic: 'mit Verkehr',
  etaNoLocation: 'Ihr Standort ist nicht verfügbar. Bitte den Zugriff erlauben.',
  addressUnknown: 'Adresse unbekannt',
  shareCopied: 'Link kopiert',
  shareUntil: 'gültig bis',
  shareGone: 'Der Link gilt nicht mehr. Bitten Sie um einen neuen.',
  shareRevoke: 'Link zurückziehen',
  notifyEnable: 'Meldungen im Browser einschalten',
  notifyOn: 'Meldungen sind eingeschaltet',
  notifyBlocked: 'Der Browser hat Meldungen für diese Seite gesperrt.',
  subscribe: 'Meldungen für diese Person einschalten',
  csv: {
    vehicle: 'Fahrzeug',
    start: 'Beginn',
    end: 'Ende',
    duration: 'Dauer, Std.',
    distance: 'Strecke, km',
    avgSpeed: 'Durchschnitt, km/h',
    maxSpeed: 'Maximum, km/h',
    addressFrom: 'Startort',
    addressTo: 'Zielort',
    address: 'Ort',
    engineHours: 'Motorstunden, Std.',
    fuelUsed: 'Verbraucht seit Einbau, l',
    time: 'Zeitpunkt',
    event: 'Ereignis',
    latitude: 'Breite',
    longitude: 'Länge',
    speed: 'Geschwindigkeit, km/h',
    ignition: 'Zündung',
    relay: 'Sperrrelais',
    energy: 'Kraftstoff/Ladung, %',
  },
  mng: {
    vehicles: 'Fahrzeuge',
    people: 'Personen',
    addVehicle: 'Fahrzeug hinzufügen',
    name: 'Name',
    fullName: 'Name',
    imei: 'IMEI',
    vin: 'VIN',
    swapTracker: 'Tracker tauschen',
    swapTrackerOpen: 'Tracker tauschen…',
    swapTrackerDo: 'Tracker jetzt tauschen',
    swapTrackerHint: 'Neues Gerät im Fahrzeug: Laufleistung und Verlauf bleiben beim Fahrzeug.',
    swapTrackerDone: 'Tracker getauscht.',
    plate: 'Kennzeichen',
    brand: 'Marke',
    brandUnset: 'Marke offenlassen',
    model: 'Modell',
    modelHint: 'z. B. Sprinter',
    tank: 'Tankvolumen, l',
    tankHint: 'Daraus werden die Liter im Tank berechnet, wenn das Fahrzeug nur Prozent meldet.',
    tankAsk: 'Dieses Fahrzeug meldet nur Prozent. Liter werden nicht berechnet, solange das Tankvolumen fehlt.',
    tankFromLink: 'Das Tankvolumen wird in KEPwise gepflegt. Ändern Sie es dort in den Fahrzeugdaten.',
    tankUnset: 'Tankvolumen fehlt',
    login: 'Benutzername',
    password: 'Passwort',
    newPassword: 'Neues Passwort',
    role: 'Rolle',
    roleAdmin: 'Administrator',
    roleDispatcher: 'Disponent',
    roleManager: 'Leitung',
    roleAdminHint: 'Alles, auch Fahrzeuge und Personen',
    roleDispatcherHint: 'Eigene Fahrzeuge und Startsperre',
    roleManagerHint: 'Eigene Fahrzeuge, nur ansehen',
    access: 'Zugriff auf Fahrzeuge',
    noAccess: 'Noch keine Fahrzeuge zugewiesen.',
    disabled: 'Gesperrt',
    enable: 'Freigeben',
    disable: 'Sperren',
    save: 'Speichern',
    remove: 'Löschen',
    removeVehicle: 'Fahrzeug löschen?',
    removeVehicleText: 'Verlauf und Strecken gehen mit verloren.',
    removePerson: 'Person löschen?',
    removePersonText: 'Der Zugang wird sofort entzogen.',
    saved: 'Gespeichert',
    noPeople: 'Noch keine Personen angelegt.',
    invitePerson: 'Person einladen',
    inviteEmail: 'E-Mail',
    inviteHint: 'Die Person erhält eine E-Mail mit einem Link und legt ihr Passwort selbst fest.',
    inviteSend: 'Einladung senden',
    invited: 'Eingeladen',
    inviteUnsent: 'E-Mail nicht gesendet',
    inviteExpired: 'Einladung abgelaufen',
    inviteResend: 'Einladung erneut senden',
    inviteRevoke: 'Einladung zurückziehen',
    inviteSentOk: 'Einladung gesendet.',
    zones: 'Zonen',
    schedule: 'Zeitplan für die Sperre',
    scheduleOff: 'Kein Zeitplan',
    scheduleNotRunning: 'Der Zeitplan läuft derzeit nicht — es wird nichts nach Zeitplan gesperrt.',
    workFrom: 'Schicht von',
    workUntil: 'Schicht bis',
    workDays: 'Arbeitstage',
    weekdays: ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'],
    journal: 'Protokoll',
    journalEmpty: 'In diesem Zeitraum wurde nichts gesperrt.',
    didBlock: 'hat den Start gesperrt',
    didUnblock: 'hat die Sperre aufgehoben',
    notConfirmed: 'Befehl gesendet, keine Bestätigung vom Tracker',
    addZone: 'Zone hinzufügen',
    radius: 'Radius, m',
    pickOnMap: 'Stelle auf der Karte antippen',
    pickAgain: 'Andere Stelle wählen',
    zoneWatchers: 'Überwachte Zonen',
    removeZone: 'Zone löschen?',
    removeZoneText: 'Meldungen über Ein- und Ausfahrt entfallen.',
    notACircle: 'Kein Kreis — wird in Traccar bearbeitet',
    noZones: 'Noch keine Zonen angelegt.',
  },

  companies: 'Firmen',
  companiesEmpty: 'Noch keine Firma angelegt.',
  companiesNew: 'Firma anlegen',
  companyName: 'Name',
  ownerEmail: 'E-Mail des Inhabers',
  deviceLimit: 'Fahrzeuglimit',
  userLimit: 'Personenlimit',
  expiry: 'Laufzeit',
  unlimited: 'unbegrenzt',
  create: 'Anlegen',
  disable: 'Ausschalten',
  enable: 'Einschalten',
  termDaysLeft: (days: number) => `noch ${days} Tage`,
  termExpired: 'Laufzeit abgelaufen',
  termUnlimited: 'unbefristet',
  machinesOfLimit: (n: number, limit: string) => `${n} von ${limit}`,
  people: 'Personen',
  never: 'nie angemeldet',
  invitePending: 'Einladung gesendet',
  inviteUnsent: 'E-Mail nicht gesendet',

  load: 'Auslastung',
  journal: 'Journal',
  journalEmpty: 'Noch keine Einträge.',
  healthVersion: 'Version',
  healthUptime: 'Betriebszeit',
  healthRooms: 'Firmen',
  healthDevices: 'Fahrzeuge',
  healthOnline: 'Online',
  healthLastPosition: 'Letzte Position',
  actCreateOrg: 'Firma angelegt',
  actDisable: 'ausgeschaltet',
  actEnable: 'eingeschaltet',
  actSetLimits: 'Limits geändert',
  actIssueKey: 'Schlüssel ausgestellt',
  actRevokeKey: 'Schlüssel widerrufen',
  issueKey: 'Schlüssel ausstellen',
  keyShownOnce: 'Der Schlüssel wird nur einmal angezeigt — jetzt kopieren',
  keyName: 'Schlüsselname',
}

const roMD: Dict = {
  appName: 'Parc auto',
  signIn: 'Autentificare',
  signInSub: 'Administrarea parcului auto',
  email: 'Utilizator',
  password: 'Parolă',
  signInAction: 'Intră',
  signingIn: 'Se conectează…',
  inviteHello: (name) => `Bună, ${name}`,
  inviteSetPassword: 'Alegeți-vă parola',
  inviteRepeat: 'Repetați parola',
  inviteMismatch: 'Parolele nu coincid.',
  invitePasswordRule: 'Cel puțin 10 caractere.',
  inviteDone: 'Parola a fost setată. Acum vă puteți autentifica.',
  inviteGone: 'Acest link nu mai este valabil. Cereți unul nou.',
  inviteGo: 'Spre autentificare',
  signOut: 'Ieșire',
  twoStep: 'Autentificare în doi pași',
  twoStepOn: 'Activată',
  twoStepOff: 'Dezactivează',
  twoStepCode: 'Codul din aplicație',
  twoStepHint: 'Adăugați cheia în aplicația de autentificare și introduceți codul curent.',
  twoStepKey: 'Cheie',
  twoStepOpenApp: 'Deschide în aplicație',
  twoStepNeeded: 'Introduceți codul din aplicația de autentificare.',
  twoStepHave: 'Am autentificare în doi pași',
  profile: 'Cont',

  fleet: 'Vehicule',
  events: 'Evenimente',
  reports: 'Rapoarte',
  manage: 'Administrare',

  moving: 'în mișcare',
  parked: 'staționează',
  blocked: 'pornire blocată',
  quiet: 'fără date noi',
  quietSince: (when) => `fără date noi din ${when}`,

  speed: 'km/h',
  voltage: 'volți',
  voltageRow: 'Tensiune',
  odometer: 'km parcurși',
  fuel: 'combustibil',
  charge: 'încărcare',
  chargeState: 'stare încărcare',
  chargeOn: 'se încarcă',
  chargePlugged: 'cablu conectat',
  chargeOff: 'neconectat',
  refreshAction: 'Actualizează poziția',
  refreshAsked: 'Se actualizează — mașina raportează',
  ignition: 'Contact',
  relay: 'Releu de blocare',
  lastSeen: 'Ultimul contact',
  on: 'pornit',
  off: 'oprit',
  relayRaised: 'activat',
  relayClear: 'eliberat',
  unknown: 'necunoscut',

  back: 'Vehicule',
  noVehicles: 'Nu vă este atribuit niciun vehicul.',
  comingSoon: 'Această secțiune se construiește într-o etapă ulterioară.',
  networkError: 'Serverul nu răspunde. Încercați peste un minut.',
  unclearError: 'Serverul a respins cererea. Verificați datele sau încercați mai târziu.',
  language: 'Limbă',
  theme: 'Temă',
  themeLight: 'Luminos',
  themeDark: 'Întunecat',
  themeSystem: 'Ca în sistem',
  sheetHandle: 'Schimbă înălțimea listei',
  backToVehicle: 'Înapoi la vehicul',
  whereAmI: 'Locația mea',
  blockAction: 'Blochează pornirea',
  unblockAction: 'Deblochează',
  confirmBlockTitle: 'Blocați pornirea?',
  confirmBlockText: 'nu va mai putea porni până la deblocare.',
  confirmUnblockTitle: 'Deblocați?',
  confirmUnblockText: 'va putea porni din nou. Comanda pleacă imediat spre tracker.',
  engineWarning: 'Un motor deja pornit continuă să funcționeze — sistemul nu oprește un vehicul în mers.',
  confirmYes: 'Da, blochează',
  confirmAction: 'Confirmă',
  confirmNo: 'Anulează',
  working: 'Se execută…',
  period: 'Perioadă',
  last24h: '24 de ore',
  last7d: '7 zile',
  last30d: '30 de zile',
  trackTooLong: (days: number) =>
    `Traseul se afișează pentru cel mult ${days} zile. Alegeți o perioadă mai scurtă.`,
  trips: 'Curse',
  stops: 'Staționări',
  summary: 'Sumar',
  track: 'Traseu',
  showTrack: 'Traseul pe hartă',
  hideTrack: 'Ascunde traseul',
  nothingFound: 'Nu sunt date pentru această perioadă.',
  loading: 'Se încarcă…',
  distance: 'Distanță',
  duration: 'Durată',
  avgSpeed: 'medie',
  maxSpeed: 'max.',
  engineHours: 'Ore motor',
  from: 'de la',
  to: 'până la',
  search: 'Caută vehicul',
  searchPlate: 'Număr, marcă, model',
  nothingMatches: 'Niciun vehicul cu acest nume.',
  pickVehicle: 'Alegeți un vehicul din listă.',
  fleetTotal: 'Total pe parc',
  scheduleRun: 'Program',
  runBlocked: 'blocate',
  runReleased: 'eliberate',
  runRefused: 'refuzate',
  runFailed: 'erori',
  eventTypes: {
    deviceOnline: 'a apărut în rețea',
    deviceOffline: 'fără date noi',
    deviceMoving: 'a pornit',
    deviceStopped: 's-a oprit',
    deviceOverspeed: 'viteză depășită',
    geofenceEnter: 'a intrat în zonă',
    geofenceExit: 'a ieșit din zonă',
    ignitionOn: 'contact pornit',
    ignitionOff: 'contact oprit',
    commandResult: 'răspunsul trackerului',
    queuedCommandSent: 'comandă livrată',
    unaccompaniedMotion: 'mișcare fără contact',
    alarm: 'alarmă',
  },
  alarms: {
    tow: 'remorcare detectată',
    powerCut: 'alimentare deconectată',
    jamming: 'bruiaj GSM',
    accident: 'impact detectat',
  },
  unitKm: 'km',
  unitLiter: 'l',
  rpm: 'rot/min',
  fuelUsedSinceInstall: 'Consumat de la montare',
  tankMissing: 'Litrii din rezervor nu se calculează: lipsește volumul rezervorului. Completați-l în datele vehiculului.',
  unitMeter: 'm',
  unitHour: 'h',
  unitMinute: 'min',
  exportAction: 'Salvează în CSV',
  exportTrack: 'Traseul în CSV',
  exporting: 'Se pregătește…',
  shareAction: 'Partajează locația',
  shareHowLong: 'Pentru cât timp?',
  routeAction: 'Traseu până la mașină',
  routeApple: 'Apple Maps',
  routeGoogle: 'Google Maps',
  routeFixed: 'Traseu către ultimul punct cunoscut. Dacă mașina pleacă, nu se actualizează.',
  etaAction: 'Calculează timpul',
  etaWithTraffic: 'cu trafic',
  etaNoLocation: 'Locația dvs. nu este disponibilă. Permiteți accesul.',
  addressUnknown: 'Adresă necunoscută',
  shareCopied: 'Link copiat',
  shareUntil: 'valabil până la',
  shareGone: 'Linkul nu mai este valabil. Cereți unul nou.',
  shareRevoke: 'Retrage linkul',
  notifyEnable: 'Activează notificările în browser',
  notifyOn: 'Notificările sunt active',
  notifyBlocked: 'Browserul a blocat notificările pentru acest site.',
  subscribe: 'Activează notificările pentru această persoană',
  csv: {
    vehicle: 'Vehicul',
    start: 'Început',
    end: 'Sfârșit',
    duration: 'Durată, h',
    distance: 'Distanță, km',
    avgSpeed: 'Viteză medie, km/h',
    maxSpeed: 'Viteză maximă, km/h',
    addressFrom: 'Plecare',
    addressTo: 'Sosire',
    address: 'Loc',
    engineHours: 'Ore motor, h',
    fuelUsed: 'Consumat de la montare, l',
    time: 'Moment',
    event: 'Eveniment',
    latitude: 'Latitudine',
    longitude: 'Longitudine',
    speed: 'Viteză, km/h',
    ignition: 'Contact',
    relay: 'Releu de blocare',
    energy: 'combustibil/încărcare, %',
  },
  mng: {
    vehicles: 'Vehicule',
    people: 'Persoane',
    addVehicle: 'Adaugă vehicul',
    name: 'Denumire',
    fullName: 'Nume',
    imei: 'IMEI',
    vin: 'VIN',
    swapTracker: 'Schimbă trackerul',
    swapTrackerOpen: 'Schimbă trackerul…',
    swapTrackerDo: 'Schimbă trackerul acum',
    swapTrackerHint: 'Dispozitiv nou în vehicul: rulajul și istoricul rămân la vehicul.',
    swapTrackerDone: 'Tracker schimbat.',
    plate: 'Număr de înmatriculare',
    brand: 'Marcă',
    brandUnset: 'Fără marcă',
    model: 'Model',
    modelHint: 'de ex. Sprinter',
    tank: 'Volumul rezervorului, l',
    tankHint: 'Din el se calculează litrii din rezervor, dacă vehiculul transmite doar procente.',
    tankAsk: 'Acest vehicul transmite doar procente. Litrii nu se calculează cât timp lipsește volumul rezervorului.',
    tankFromLink: 'Volumul rezervorului este gestionat în KEPwise. Modificați-l acolo, în datele vehiculului.',
    tankUnset: 'fără volumul rezervorului',
    login: 'Utilizator',
    password: 'Parolă',
    newPassword: 'Parolă nouă',
    role: 'Rol',
    roleAdmin: 'Administrator',
    roleDispatcher: 'Dispecer',
    roleManager: 'Conducere',
    roleAdminHint: 'Tot, inclusiv vehicule și persoane',
    roleDispatcherHint: 'Vehiculele proprii și blocarea pornirii',
    roleManagerHint: 'Vehiculele proprii, doar vizualizare',
    access: 'Acces la vehicule',
    noAccess: 'Niciun vehicul atribuit.',
    disabled: 'Blocat',
    enable: 'Deblochează',
    disable: 'Blochează',
    save: 'Salvează',
    remove: 'Șterge',
    removeVehicle: 'Ștergeți vehiculul?',
    removeVehicleText: 'Istoricul și traseele se pierd odată cu el.',
    removePerson: 'Ștergeți persoana?',
    removePersonText: 'Accesul se retrage imediat.',
    saved: 'Salvat',
    noPeople: 'Nicio persoană creată.',
    invitePerson: 'Invită persoană',
    inviteEmail: 'E-mail',
    inviteHint: 'Persoana primește un e-mail cu un link și își alege singură parola.',
    inviteSend: 'Trimite invitația',
    invited: 'Invitat',
    inviteUnsent: 'e-mailul nu a plecat',
    inviteExpired: 'invitația a expirat',
    inviteResend: 'Retrimite invitația',
    inviteRevoke: 'Retrage invitația',
    inviteSentOk: 'Invitația a fost trimisă.',
    zones: 'Zone',
    schedule: 'Program de blocare',
    scheduleOff: 'Fără program',
    scheduleNotRunning: 'Programul nu funcționează acum — blocarea programată nu se va executa.',
    workFrom: 'Tura de la',
    workUntil: 'Tura până la',
    workDays: 'Zile lucrătoare',
    weekdays: ['Lu', 'Ma', 'Mi', 'Jo', 'Vi', 'Sâ', 'Du'],
    journal: 'Jurnal',
    journalEmpty: 'În această perioadă nu s-a blocat nimic.',
    didBlock: 'a blocat pornirea',
    didUnblock: 'a deblocat',
    notConfirmed: 'Comandă trimisă, fără confirmare de la tracker',
    addZone: 'Adaugă zonă',
    radius: 'Rază, m',
    pickOnMap: 'Atingeți locul pe hartă',
    pickAgain: 'Alegeți alt loc',
    zoneWatchers: 'Zone urmărite',
    removeZone: 'Ștergeți zona?',
    removeZoneText: 'Semnalele de intrare și ieșire dispar.',
    notACircle: 'Nu este cerc — se editează în Traccar',
    noZones: 'Nicio zonă creată.',
  },

  companies: 'Companii',
  companiesEmpty: 'Nicio firmă creată.',
  companiesNew: 'Înființează firmă',
  companyName: 'Denumire',
  ownerEmail: 'E-mailul proprietarului',
  deviceLimit: 'Limită vehicule',
  userLimit: 'Limită persoane',
  expiry: 'Termen',
  unlimited: 'fără limită',
  create: 'Înființează',
  disable: 'Dezactivează',
  enable: 'Activează',
  termDaysLeft: (days: number) => `mai sunt ${days} zile`,
  termExpired: 'termenul a expirat',
  termUnlimited: 'nelimitat',
  machinesOfLimit: (n: number, limit: string) => `${n} din ${limit}`,
  people: 'Persoane',
  never: 'nu s-a autentificat',
  invitePending: 'invitație trimisă',
  inviteUnsent: 'e-mailul nu a plecat',

  load: 'Încărcare',
  journal: 'Jurnal',
  journalEmpty: 'Încă nu sunt înregistrări.',
  healthVersion: 'Versiune',
  healthUptime: 'Timp de funcționare',
  healthRooms: 'Companii',
  healthDevices: 'Vehicule',
  healthOnline: 'Online',
  healthLastPosition: 'Ultima poziție',
  actCreateOrg: 'a înființat firma',
  actDisable: 'a dezactivat',
  actEnable: 'a activat',
  actSetLimits: 'a schimbat limitele',
  actIssueKey: 'a emis cheia',
  actRevokeKey: 'a revocat cheia',
  issueKey: 'Emite cheie',
  keyShownOnce: 'Cheia se afișează o singură dată — copiați-o acum',
  keyName: 'Denumirea cheii',
}

const ru: Dict = {
  appName: 'Автопарк',
  signIn: 'Вход',
  signInSub: 'Мониторинг автопарка',
  email: 'Логин',
  password: 'Пароль',
  signInAction: 'Войти',
  signingIn: 'Входим…',
  inviteHello: (name) => `Здравствуйте, ${name}`,
  inviteSetPassword: 'Назначьте себе пароль',
  inviteRepeat: 'Повторите пароль',
  inviteMismatch: 'Пароли не совпадают.',
  invitePasswordRule: 'Не короче 10 знаков.',
  inviteDone: 'Пароль назначен. Теперь войдите.',
  inviteGone: 'Ссылка больше не действует. Попросите новую.',
  inviteGo: 'Ко входу',
  signOut: 'Выйти',
  twoStep: 'Вход в два шага',
  twoStepOn: 'Включён',
  twoStepOff: 'Выключить',
  twoStepCode: 'Код из приложения',
  twoStepHint: 'Внесите ключ в приложение-аутентификатор и введите текущий код.',
  twoStepKey: 'Ключ',
  twoStepOpenApp: 'Открыть в приложении',
  twoStepNeeded: 'Введите код из приложения-аутентификатора.',
  twoStepHave: 'У меня включён вход в два шага',
  profile: 'Учётная запись',

  fleet: 'Машины',
  events: 'События',
  reports: 'Отчёты',
  manage: 'Управление',

  moving: 'в движении',
  parked: 'стоит',
  blocked: 'запуск заблокирован',
  quiet: 'нет свежих данных',
  quietSince: (when) => `нет свежих данных с ${when}`,

  speed: 'км/ч',
  voltage: 'вольт',
  voltageRow: 'Напряжение',
  odometer: 'км пробег',
  fuel: 'топливо',
  charge: 'заряд',
  chargeState: 'зарядка',
  chargeOn: 'заряжается',
  chargePlugged: 'кабель подключён',
  chargeOff: 'не подключён',
  refreshAction: 'Обновить положение',
  refreshAsked: 'Обновляем — машина отвечает',
  ignition: 'Зажигание',
  relay: 'Реле блокировки',
  lastSeen: 'Последний контакт',
  on: 'включено',
  off: 'выключено',
  relayRaised: 'поднято',
  relayClear: 'снято',
  unknown: 'неизвестно',

  back: 'Машины',
  noVehicles: 'Вам пока не выдано ни одной машины.',
  comingSoon: 'Этот раздел строится на следующем этапе.',
  networkError: 'Сервер не отвечает. Повторите через минуту.',
  unclearError: 'Сервер отклонил запрос. Проверьте данные или повторите позже.',
  language: 'Язык',
  theme: 'Тема',
  themeLight: 'Светлая',
  themeDark: 'Тёмная',
  themeSystem: 'Как в системе',
  sheetHandle: 'Изменить высоту списка',
  backToVehicle: 'Вернуться к машине',
  whereAmI: 'Где я',
  blockAction: 'Заблокировать запуск',
  unblockAction: 'Снять блокировку',
  confirmBlockTitle: 'Заблокировать запуск?',
  confirmBlockText: 'не сможет завестись, пока блокировку не снимут.',
  confirmUnblockTitle: 'Снять блокировку?',
  confirmUnblockText: 'снова сможет завестись. Команда уйдёт на трекер сразу.',
  engineWarning: 'Если двигатель уже работает, он продолжит работать — система не глушит машину на ходу.',
  confirmYes: 'Да, заблокировать',
  confirmAction: 'Подтвердить',
  confirmNo: 'Отмена',
  working: 'Выполняем…',
  period: 'Период',
  last24h: 'сутки',
  last7d: '7 дней',
  last30d: '30 дней',
  trackTooLong: (days: number) =>
    `Трек показывается не глубже ${days} суток. Выберите период короче.`,
  trips: 'Поездки',
  stops: 'Стоянки',
  summary: 'Сводка',
  track: 'Трек',
  showTrack: 'Показать трек на карте',
  hideTrack: 'Убрать трек',
  nothingFound: 'За этот период данных нет.',
  loading: 'Загружаем…',
  distance: 'Пробег',
  duration: 'Длительность',
  avgSpeed: 'средняя',
  maxSpeed: 'макс.',
  engineHours: 'Моточасы',
  from: 'с',
  to: 'по',
  search: 'Найти машину',
  searchPlate: 'Госномер, марка, модель',
  nothingMatches: 'Машины с таким именем нет.',
  pickVehicle: 'Выберите машину из списка.',
  fleetTotal: 'Итого по парку',
  scheduleRun: 'Расписание',
  runBlocked: 'заперто',
  runReleased: 'отпущено',
  runRefused: 'отказано',
  runFailed: 'сбоев',
  eventTypes: {
    deviceOnline: 'появилась на связи',
    deviceOffline: 'нет новых данных',
    deviceMoving: 'начала движение',
    deviceStopped: 'остановилась',
    deviceOverspeed: 'превышение скорости',
    geofenceEnter: 'въезд в зону',
    geofenceExit: 'выезд из зоны',
    ignitionOn: 'зажигание включено',
    ignitionOff: 'зажигание выключено',
    commandResult: 'ответ трекера',
    queuedCommandSent: 'команда доставлена',
    unaccompaniedMotion: 'движение без зажигания',
    alarm: 'тревога',
  },
  alarms: {
    tow: 'буксировка',
    powerCut: 'снято питание',
    jamming: 'глушат связь',
    accident: 'удар',
  },
  unitKm: 'км',
  unitLiter: 'л',
  rpm: 'об/мин',
  fuelUsedSinceInstall: 'Израсходовано с установки',
  tankMissing: 'Литры в баке не считаем: не указан объём бака. Укажите его в карточке машины.',
  unitMeter: 'м',
  unitHour: 'ч',
  unitMinute: 'мин',
  exportAction: 'Сохранить в CSV',
  exportTrack: 'Трек в CSV',
  exporting: 'Готовим файл…',
  shareAction: 'Поделиться геолокацией',
  shareHowLong: 'На какой срок?',
  routeAction: 'Проложить маршрут',
  routeApple: 'Apple Карты',
  routeGoogle: 'Google Карты',
  routeFixed: 'Маршрут ведёт к последней известной точке. Если машина поедет, он не обновится.',
  etaAction: 'Сколько ехать',
  etaWithTraffic: 'с учётом пробок',
  etaNoLocation: 'Ваше положение недоступно. Разрешите доступ к геолокации.',
  addressUnknown: 'Адрес неизвестен',
  shareCopied: 'Ссылка скопирована',
  shareUntil: 'действует до',
  shareGone: 'Ссылка больше не действует. Попросите новую.',
  shareRevoke: 'Отозвать ссылку',
  notifyEnable: 'Включить уведомления в браузере',
  notifyOn: 'Уведомления включены',
  notifyBlocked: 'Браузер запретил уведомления для этого сайта.',
  subscribe: 'Включить уведомления этому человеку',
  csv: {
    vehicle: 'Машина',
    start: 'Начало',
    end: 'Конец',
    duration: 'Длительность, ч',
    distance: 'Пробег, км',
    avgSpeed: 'Средняя, км/ч',
    maxSpeed: 'Максимальная, км/ч',
    addressFrom: 'Откуда',
    addressTo: 'Куда',
    address: 'Место',
    engineHours: 'Моточасы, ч',
    fuelUsed: 'Израсходовано с установки, л',
    time: 'Время',
    event: 'Событие',
    latitude: 'Широта',
    longitude: 'Долгота',
    speed: 'Скорость, км/ч',
    ignition: 'Зажигание',
    relay: 'Реле блокировки',
    energy: 'топливо/заряд, %',
  },
  mng: {
    vehicles: 'Машины',
    people: 'Люди',
    addVehicle: 'Добавить машину',
    name: 'Название',
    fullName: 'Имя',
    imei: 'IMEI',
    vin: 'VIN',
    swapTracker: 'Заменить трекер',
    swapTrackerOpen: 'Заменить трекер…',
    swapTrackerDo: 'Заменить трекер сейчас',
    swapTrackerHint: 'Новое устройство в машине: пробег и история остаются у машины.',
    swapTrackerDone: 'Трекер заменён.',
    plate: 'Госномер',
    brand: 'Марка',
    brandUnset: 'Не указывать',
    model: 'Модель',
    modelHint: 'например, Sprinter',
    tank: 'Объём бака, л',
    tankHint: 'По нему считаются литры в баке, если машина передаёт только проценты.',
    tankAsk: 'Эта машина передаёт только проценты. Литры не считаем, пока не указан объём бака.',
    tankFromLink: 'Объём бака задаётся в KEPwise. Изменить его можно там, в карточке машины.',
    tankUnset: 'объём бака не указан',
    login: 'Логин',
    password: 'Пароль',
    newPassword: 'Новый пароль',
    role: 'Роль',
    roleAdmin: 'Администратор',
    roleDispatcher: 'Диспетчер',
    roleManager: 'Руководитель',
    roleAdminHint: 'Всё, включая машины и людей',
    roleDispatcherHint: 'Свои машины и блокировка запуска',
    roleManagerHint: 'Свои машины, только просмотр',
    access: 'Доступ к машинам',
    noAccess: 'Машины пока не выданы.',
    disabled: 'Отключён',
    enable: 'Включить',
    disable: 'Отключить',
    save: 'Сохранить',
    remove: 'Удалить',
    removeVehicle: 'Удалить машину?',
    removeVehicleText: 'Вместе с ней пропадут история и треки.',
    removePerson: 'Удалить человека?',
    removePersonText: 'Доступ пропадёт сразу.',
    saved: 'Сохранено',
    noPeople: 'Пока никого не заведено.',
    invitePerson: 'Пригласить человека',
    inviteEmail: 'Почта',
    inviteHint: 'Человек получит письмо со ссылкой и назначит себе пароль сам.',
    inviteSend: 'Отправить приглашение',
    invited: 'Приглашён',
    inviteUnsent: 'письмо не ушло',
    inviteExpired: 'приглашение истекло',
    inviteResend: 'Отправить приглашение заново',
    inviteRevoke: 'Отозвать приглашение',
    inviteSentOk: 'Приглашение отправлено.',
    zones: 'Зоны',
    schedule: 'Расписание блокировки',
    scheduleOff: 'Расписания нет',
    scheduleNotRunning: 'Расписание сейчас не работает — блокировка по нему не сработает.',
    workFrom: 'Смена с',
    workUntil: 'Смена до',
    workDays: 'Рабочие дни',
    weekdays: ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'],
    journal: 'Журнал',
    journalEmpty: 'За этот период ничего не блокировали.',
    didBlock: 'заблокировал запуск',
    didUnblock: 'снял блокировку',
    notConfirmed: 'команда ушла, подтверждения от трекера нет',
    addZone: 'Добавить зону',
    radius: 'Радиус, м',
    pickOnMap: 'Укажите место на карте',
    pickAgain: 'Выбрать другое место',
    zoneWatchers: 'Зоны, за которыми следим',
    removeZone: 'Удалить зону?',
    removeZoneText: 'Сигналы о въезде и выезде пропадут.',
    notACircle: 'Нарисована не кругом — правится в Traccar',
    noZones: 'Зон пока нет.',
  },

  companies: 'Компании',
  companiesEmpty: 'Фирм пока нет.',
  companiesNew: 'Завести фирму',
  companyName: 'Название',
  ownerEmail: 'Почта владельца',
  deviceLimit: 'Лимит машин',
  userLimit: 'Лимит сотрудников',
  expiry: 'Срок',
  unlimited: 'без предела',
  create: 'Завести',
  disable: 'Выключить',
  enable: 'Включить',
  termDaysLeft: (days: number) => `осталось ${days} дн.`,
  termExpired: 'срок истёк',
  termUnlimited: 'бессрочно',
  machinesOfLimit: (n: number, limit: string) => `${n} из ${limit}`,
  people: 'Сотрудников',
  never: 'не входил',
  invitePending: 'приглашение отправлено',
  inviteUnsent: 'письмо не ушло',

  load: 'Нагрузка',
  journal: 'Журнал',
  journalEmpty: 'Записей пока нет.',
  healthVersion: 'Версия',
  healthUptime: 'Аптайм',
  healthRooms: 'Комнат',
  healthDevices: 'Машин',
  healthOnline: 'На связи',
  healthLastPosition: 'Последняя позиция',
  actCreateOrg: 'завёл фирму',
  actDisable: 'выключил',
  actEnable: 'включил',
  actSetLimits: 'изменил лимиты',
  actIssueKey: 'выдал ключ',
  actRevokeKey: 'отозвал ключ',
  issueKey: 'Выдать ключ',
  keyShownOnce: 'Ключ виден один раз — скопируйте',
  keyName: 'Название ключа',
}

const en: Dict = {
  appName: 'Fleet',
  signIn: 'Sign in',
  signInSub: 'Fleet monitoring',
  email: 'Username',
  password: 'Password',
  signInAction: 'Sign in',
  signingIn: 'Signing in…',
  inviteHello: (name) => `Hello, ${name}`,
  inviteSetPassword: 'Set your password',
  inviteRepeat: 'Repeat password',
  inviteMismatch: 'The passwords do not match.',
  invitePasswordRule: 'At least 10 characters.',
  inviteDone: 'Password set. You can sign in now.',
  inviteGone: 'This link is no longer valid. Ask for a new one.',
  inviteGo: 'Go to sign-in',
  signOut: 'Sign out',
  twoStep: 'Two-step sign-in',
  twoStepOn: 'On',
  twoStepOff: 'Turn off',
  twoStepCode: 'Code from the app',
  twoStepHint: 'Add the key to your authenticator app and enter the current code.',
  twoStepKey: 'Key',
  twoStepOpenApp: 'Open in the app',
  twoStepNeeded: 'Enter the code from your authenticator app.',
  twoStepHave: 'I have two-step sign-in',
  profile: 'Account',

  fleet: 'Vehicles',
  events: 'Events',
  reports: 'Reports',
  manage: 'Management',

  moving: 'moving',
  parked: 'parked',
  blocked: 'start blocked',
  quiet: 'no fresh data',
  quietSince: (when) => `no fresh data since ${when}`,

  speed: 'km/h',
  voltage: 'volts',
  voltageRow: 'Voltage',
  odometer: 'km total',
  fuel: 'fuel',
  charge: 'charge',
  chargeState: 'charging',
  chargeOn: 'charging now',
  chargePlugged: 'cable plugged',
  chargeOff: 'unplugged',
  refreshAction: 'Update location',
  refreshAsked: 'Updating — vehicle is reporting',
  ignition: 'Ignition',
  relay: 'Blocking relay',
  lastSeen: 'Last contact',
  on: 'on',
  off: 'off',
  relayRaised: 'engaged',
  relayClear: 'released',
  unknown: 'unknown',

  back: 'Vehicles',
  noVehicles: 'No vehicles have been assigned to you yet.',
  comingSoon: 'This section is built in a later stage.',
  networkError: 'The server is not responding. Please try again in a minute.',
  unclearError: 'The server refused the request. Check the details or try again later.',
  language: 'Language',
  theme: 'Appearance',
  themeLight: 'Light',
  themeDark: 'Dark',
  themeSystem: 'Like the system',
  sheetHandle: 'Resize the list',
  backToVehicle: 'Back to the vehicle',
  whereAmI: 'My location',
  blockAction: 'Block starting',
  unblockAction: 'Remove block',
  confirmBlockTitle: 'Block starting?',
  confirmBlockText: 'will not be able to start until the block is removed.',
  confirmUnblockTitle: 'Remove the block?',
  confirmUnblockText: 'will be able to start again. The command goes to the tracker at once.',
  engineWarning: 'An engine that is already running keeps running — the system never stops a moving vehicle.',
  confirmYes: 'Yes, block it',
  confirmAction: 'Confirm',
  confirmNo: 'Cancel',
  working: 'Working…',
  period: 'Period',
  last24h: '24 hours',
  last7d: '7 days',
  last30d: '30 days',
  trackTooLong: (days: number) =>
    `The track is shown for at most ${days} days. Pick a shorter period.`,
  trips: 'Trips',
  stops: 'Stops',
  summary: 'Summary',
  track: 'Track',
  showTrack: 'Show track on the map',
  hideTrack: 'Hide track',
  nothingFound: 'No data for this period.',
  loading: 'Loading…',
  distance: 'Distance',
  duration: 'Duration',
  avgSpeed: 'avg',
  maxSpeed: 'max',
  engineHours: 'Engine hours',
  from: 'from',
  to: 'to',
  search: 'Find a vehicle',
  searchPlate: 'Plate, make, model',
  nothingMatches: 'No vehicle with that name.',
  pickVehicle: 'Pick a vehicle from the list.',
  fleetTotal: 'Fleet total',
  scheduleRun: 'Schedule',
  runBlocked: 'blocked',
  runReleased: 'released',
  runRefused: 'refused',
  runFailed: 'failed',
  eventTypes: {
    deviceOnline: 'came online',
    deviceOffline: 'no new data',
    deviceMoving: 'started moving',
    deviceStopped: 'stopped',
    deviceOverspeed: 'speeding',
    geofenceEnter: 'entered zone',
    geofenceExit: 'left zone',
    ignitionOn: 'ignition on',
    ignitionOff: 'ignition off',
    commandResult: 'tracker reply',
    queuedCommandSent: 'command delivered',
    unaccompaniedMotion: 'moving without ignition',
    alarm: 'alarm',
  },
  alarms: {
    tow: 'towing detected',
    powerCut: 'power disconnected',
    jamming: 'GSM jamming',
    accident: 'impact detected',
  },
  unitKm: 'km',
  unitLiter: 'l',
  rpm: 'rpm',
  fuelUsedSinceInstall: 'Used since install',
  tankMissing: 'Litres in the tank are not calculated: the tank volume is not set. Add it in the vehicle details.',
  unitMeter: 'm',
  unitHour: 'h',
  unitMinute: 'min',
  exportAction: 'Save as CSV',
  exportTrack: 'Track as CSV',
  exporting: 'Preparing…',
  shareAction: 'Share location',
  shareHowLong: 'For how long?',
  routeAction: 'Get directions',
  routeApple: 'Apple Maps',
  routeGoogle: 'Google Maps',
  routeFixed: 'Directions to the last known point. They will not follow the vehicle.',
  etaAction: 'Travel time',
  etaWithTraffic: 'with traffic',
  etaNoLocation: 'Your location is unavailable. Please allow access.',
  addressUnknown: 'Address unknown',
  shareCopied: 'Link copied',
  shareUntil: 'valid until',
  shareGone: 'This link is no longer valid. Please ask for a new one.',
  shareRevoke: 'Revoke link',
  notifyEnable: 'Turn on browser notifications',
  notifyOn: 'Notifications are on',
  notifyBlocked: 'The browser has blocked notifications for this site.',
  subscribe: 'Turn on notifications for this person',
  csv: {
    vehicle: 'Vehicle',
    start: 'Start',
    end: 'End',
    duration: 'Duration, h',
    distance: 'Distance, km',
    avgSpeed: 'Average, km/h',
    maxSpeed: 'Maximum, km/h',
    addressFrom: 'From',
    addressTo: 'To',
    address: 'Place',
    engineHours: 'Engine hours, h',
    fuelUsed: 'Used since install, l',
    time: 'Time',
    event: 'Event',
    latitude: 'Latitude',
    longitude: 'Longitude',
    speed: 'Speed, km/h',
    ignition: 'Ignition',
    relay: 'Blocking relay',
    energy: 'fuel/charge, %',
  },
  mng: {
    vehicles: 'Vehicles',
    people: 'People',
    addVehicle: 'Add vehicle',
    name: 'Name',
    fullName: 'Name',
    imei: 'IMEI',
    vin: 'VIN',
    swapTracker: 'Replace tracker',
    swapTrackerOpen: 'Replace tracker…',
    swapTrackerDo: 'Replace tracker now',
    swapTrackerHint: 'New device in the vehicle: mileage and history stay with the vehicle.',
    swapTrackerDone: 'Tracker replaced.',
    plate: 'Plate',
    brand: 'Make',
    brandUnset: 'Leave unset',
    model: 'Model',
    modelHint: 'e.g. Sprinter',
    tank: 'Tank volume, l',
    tankHint: 'Used to work out litres in the tank when the vehicle reports only a percentage.',
    tankAsk: 'This vehicle reports only a percentage. Litres are not calculated until the tank volume is set.',
    tankFromLink: 'The tank volume is managed in KEPwise. Change it there, in the vehicle details.',
    tankUnset: 'no tank volume',
    login: 'Username',
    password: 'Password',
    newPassword: 'New password',
    role: 'Role',
    roleAdmin: 'Administrator',
    roleDispatcher: 'Dispatcher',
    roleManager: 'Manager',
    roleAdminHint: 'Everything, including vehicles and people',
    roleDispatcherHint: 'Own vehicles and start blocking',
    roleManagerHint: 'Own vehicles, view only',
    access: 'Vehicle access',
    noAccess: 'No vehicles assigned yet.',
    disabled: 'Disabled',
    enable: 'Enable',
    disable: 'Disable',
    save: 'Save',
    remove: 'Delete',
    removeVehicle: 'Delete vehicle?',
    removeVehicleText: 'Its history and tracks go with it.',
    removePerson: 'Delete person?',
    removePersonText: 'Access is revoked immediately.',
    saved: 'Saved',
    noPeople: 'No people created yet.',
    invitePerson: 'Invite person',
    inviteEmail: 'Email',
    inviteHint: 'The person receives an email with a link and sets their own password.',
    inviteSend: 'Send invitation',
    invited: 'Invited',
    inviteUnsent: 'email not sent',
    inviteExpired: 'invitation expired',
    inviteResend: 'Resend invitation',
    inviteRevoke: 'Revoke invitation',
    inviteSentOk: 'Invitation sent.',
    zones: 'Zones',
    schedule: 'Blocking schedule',
    scheduleOff: 'No schedule',
    scheduleNotRunning: 'The schedule is not running right now — nothing will be locked on schedule.',
    workFrom: 'Shift from',
    workUntil: 'Shift until',
    workDays: 'Working days',
    weekdays: ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'],
    journal: 'Journal',
    journalEmpty: 'Nothing was blocked in this period.',
    didBlock: 'blocked starting',
    didUnblock: 'removed the block',
    notConfirmed: 'command sent, no confirmation from the tracker',
    addZone: 'Add zone',
    radius: 'Radius, m',
    pickOnMap: 'Tap the spot on the map',
    pickAgain: 'Pick another spot',
    zoneWatchers: 'Watched zones',
    removeZone: 'Delete zone?',
    removeZoneText: 'Entry and exit alerts will stop.',
    notACircle: 'Not a circle — edited in Traccar',
    noZones: 'No zones yet.',
  },

  companies: 'Companies',
  companiesEmpty: 'No companies yet.',
  companiesNew: 'Create company',
  companyName: 'Name',
  ownerEmail: "Owner's email",
  deviceLimit: 'Vehicle limit',
  userLimit: 'User limit',
  expiry: 'Term',
  unlimited: 'unlimited',
  create: 'Create',
  disable: 'Turn off',
  enable: 'Turn on',
  termDaysLeft: (days: number) => `${days} days left`,
  termExpired: 'term expired',
  termUnlimited: 'unlimited',
  machinesOfLimit: (n: number, limit: string) => `${n} of ${limit}`,
  people: 'People',
  never: 'never signed in',
  invitePending: 'invitation sent',
  inviteUnsent: 'email not sent',

  load: 'Load',
  journal: 'Journal',
  journalEmpty: 'No entries yet.',
  healthVersion: 'Version',
  healthUptime: 'Uptime',
  healthRooms: 'Rooms',
  healthDevices: 'Vehicles',
  healthOnline: 'Online',
  healthLastPosition: 'Last position',
  actCreateOrg: 'created the company',
  actDisable: 'turned off',
  actEnable: 'turned on',
  actSetLimits: 'changed limits',
  actIssueKey: 'issued a key',
  actRevokeKey: 'revoked a key',
  issueKey: 'Issue key',
  keyShownOnce: 'The key is shown once — copy it now',
  keyName: 'Key name',
}

const DICTS: Record<Lang, Dict> = { de, 'ro-MD': roMD, ru, en }

const STORAGE_KEY = 'telematic.lang'

export function isLang(value: unknown): value is Lang {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value)
}

/** Язык из выбора пользователя, иначе из браузера, иначе немецкий. */
/**
 * Язык, о котором точно известно, что он нужен этому человеку.
 *
 * Это либо его собственный прошлый выбор, либо язык, который назвал браузер.
 * `null` означает «неизвестно» — и тогда решать должна настройка сервера, а не
 * зашитое в код умолчание: парк в Молдове, которому поставили `ro-MD`, иначе
 * получал бы немецкий.
 *
 * Чистая функция: всё, что она знает о мире, приходит доводами. Иначе её
 * нельзя было бы проверить — ни хранилища, ни браузера в тестах нет.
 */
export function knownLang(saved: string | null, browserLanguages: readonly string[]): Lang | null {
  if (isLang(saved)) return saved
  for (const candidate of browserLanguages) {
    if (isLang(candidate)) return candidate
    if (candidate.startsWith('ro')) return 'ro-MD'
    if (candidate.startsWith('de')) return 'de'
    if (candidate.startsWith('ru')) return 'ru'
    if (candidate.startsWith('en')) return 'en'
  }
  return null
}

/** То же самое, но из настоящего браузера. Возвращает `null`, если неизвестно. */
export function browserKnownLang(): Lang | null {
  let saved: string | null = null
  try {
    saved = localStorage.getItem(STORAGE_KEY)
  } catch {
    // приватный режим или заблокированное хранилище — просто идём дальше
  }
  return knownLang(saved, navigator.languages ?? [])
}

export function initialLang(fallback: string): Lang {
  return browserKnownLang() ?? (isLang(fallback) ? fallback : 'de')
}

export function rememberLang(lang: Lang): void {
  try {
    localStorage.setItem(STORAGE_KEY, lang)
  } catch {
    // не сохранилось — не беда, выбор проживёт до конца сессии
  }
}

export function dict(lang: Lang): Dict {
  return DICTS[lang]
}
